import React, { useState, useEffect } from "react";
import AddPairModal from "../AddPairModal";
import { ConcorrentiService } from "../../services/concorrentiService";
import { Concorrente } from "../../types/concorrenti/concorrente";
import { GaraService } from "../../services/garaService";
import { Gara } from "../../types/gare/gara";

interface Props {
  gara: Gara;
}

const PartecipantiCoppieTab: React.FC<Props> = ({ gara }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [pairs, setPairs] = useState<Array<[string, string]>>([]);
  const [concorrentiDisponibili, setConcorrentiDisponibili] = useState<
    Concorrente[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false); // nuovo stato
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const loadConcorrenti = async () => {
      setLoading(true);
      try {
        const data = await ConcorrentiService.getConcorrentiList(1, 1000, "", undefined, undefined, gara?.discipline || 1);
        setConcorrentiDisponibili(data.results);
      } catch (err) {
        console.error("Errore nel caricamento concorrenti:", err);
      } finally {
        setLoading(false);
      }
    };

    const loadGroups = async () => {
      setLoadingGroups(true); // start loader gruppi
      try {
        const groups = await GaraService.getGroups(gara.id);
        // groups è un array di oggetti { groupId, competitorIds: number[] }
        // Convertiamo in Array<[string, string]>
        const loadedPairs: Array<[string, string]> = [];
        groups.forEach((group) => {
          if (group.competitors.length === 2) {
            loadedPairs.push([
              String(group.competitors[0].id),
              String(group.competitors[1].id),
            ]);
          }
        });
        setPairs(loadedPairs);
      } catch (err) {
        console.error("Errore nel caricamento delle coppie:", err);
      } finally {
        setLoadingGroups(false); // stop loader gruppi
      }
    };

    loadConcorrenti();
    loadGroups();
  }, [gara.id, gara.discipline]);

  const handleAddPair = async (pair: [string, string]) => {
    setPairs((prev) => [...prev, pair]);
    try {
      await GaraService.createGroup({
        competitionId: gara.id,
        competitorIds: [Number(pair[0]), Number(pair[1])],
      });
    } catch (err) {
      console.error("Errore nella creazione della coppia:", err);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Coppie</h2>
      <button
        className="mb-4 px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700"
        onClick={() => setModalOpen(true)}
      >
        Aggiungi Coppia
      </button>

      {/* Filtra i concorrenti già inseriti in una coppia */}
      <AddPairModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        members={concorrentiDisponibili
          .filter((c) => !pairs.flat().includes(String(c.id)))
          .map((c) => ({
            id: String(c.id),
            name: `${c.lastName} ${c.firstName}`,
          }))}
        onAddPair={handleAddPair}
      />

      <div className="mt-2">
        {loading || loadingGroups ? (
          <p className="text-gray-400 mt-2">Caricamento coppie...</p>
        ) : pairs.length === 0 ? (
          <p className="text-gray-500">Nessuna coppia aggiunta.</p>
        ) : (
          <div className="space-y-3">
            {pairs.map(([id1, id2], idx) => {
              const m1 = concorrentiDisponibili.find(
                (m) => String(m.id) === id1
              );
              const m2 = concorrentiDisponibili.find(
                (m) => String(m.id) === id2
              );
              const handleRemove = async () => {
                try {
                  // Passa anche la lista di competitorsId (id1, id2)
                  await GaraService.deleteGroup(gara.id, [Number(id1), Number(id2)]);
                  setPairs((prev) => prev.filter((p, i) => i !== idx));
                } catch (err: any) {
                    if (err?.status === 409) {
                      setRemoveError("Impossibile rimuovere la coppia: esistono dati collegati (turni, settori o classifica). Rimuovi prima i dati associati.");
                    } else {
                      setRemoveError("Errore nella rimozione della coppia.");
                    }
                    console.error("Errore nella rimozione della coppia:", err);
                  }
              };
              return (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {m1 ? `${m1.lastName} ${m1.firstName}` : id1} -{" "}
                          {m2 ? `${m2.lastName} ${m2.firstName}` : id2}
                        </h4>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleRemove}
                    className="text-red-500 hover:text-red-700 font-medium text-sm transition-colors"
                  >
                    Rimuovi
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
            {removeError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setRemoveError(null)} />
          <div className="relative bg-white rounded-lg border border-brand-200 p-6 max-w-sm mx-auto">
            <h3 className="text-lg font-bold mb-2 text-red-600">Errore</h3>
            <p className="mb-4 text-gray-700">{removeError}</p>
            <button
              className="bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded"
              onClick={() => setRemoveError(null)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
      {loading && (
        <p className="text-gray-400 mt-2">Caricamento concorrenti...</p>
      )}
    </div>
  );
};

export default PartecipantiCoppieTab;
